/**
 * Whether the model can be printed, as its own answer.
 *
 * Separate from the scale row on purpose and next to it deliberately. Scale
 * answers "how big is this in millimetres"; this answers "is this a solid a
 * slicer can turn into toolpaths". They fail independently - a perfectly
 * calibrated model with a hole in it prints as a shell full of air - and a
 * screen that merged them would tell somebody their model is print-ready
 * because they measured it with a tape.
 *
 * Three states, not two. "Needs repair" is the useful one: somebody whose
 * model is nearly printable should be told it can be fixed, not that it is
 * broken. The distinction is whether the existing surface can be reconciled
 * with itself or whether something has to be created - and where something
 * would have to be created, the honest answer is another photograph.
 *
 * The sentences come from the server. A client that mapped issue codes to
 * words would be a second place for the wording to drift, and the words are
 * the part a customer reads.
 *
 * ## And a fourth thing a slicer cannot tell you
 *
 * A mesh can be watertight, manifold, correctly oriented and entirely made up.
 * None of the checks above asks where the surface came from, so the answer to
 * that travels with the report and is shown either way round: in a
 * visual-completion project completed geometry is not a defect and is still
 * named, because somebody about to spend six hours of filament is entitled to
 * know which parts of the object were measured. In a precise project it is a
 * blocker, and the server has already said so in `readiness`.
 */

import { useEffect, useState } from "react";

import { getMeshHealth } from "@/api/client";
import type { MeshHealth } from "@/api/types";
import { Callout, StatusBadge } from "@/components/ui";

const HEADLINE: Record<MeshHealth["readiness"], string> = {
  ready: "Baskıya uygun",
  needs_repair: "Küçük onarımla baskıya uygun",
  not_ready: "Baskıya uygun değil",
};

const TONE: Record<MeshHealth["readiness"], "success" | "caution" | "danger"> = {
  ready: "success",
  needs_repair: "caution",
  not_ready: "danger",
};

export function MeshHealthPanel({ jobId }: { jobId: string }) {
  const [health, setHealth] = useState<MeshHealth | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setHealth(null);
    setFailed(false);
    // `await` inside a try, rather than `.then().catch()`. The chained form
    // leaves the original promise's rejection travelling through an
    // intermediate promise, and a test runner that watches for unhandled
    // rejections reports it before the catch runs.
    const measure = async () => {
      try {
        const result = await getMeshHealth(jobId);
        if (!cancelled) setHealth(result);
      } catch {
        if (!cancelled) setFailed(true);
      }
    };
    void measure();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  if (failed) {
    // Said rather than hidden. An absent row reads as "fine", and this row's
    // whole job is to not let somebody assume that.
    return (
      <p className="muted small">Baskı uygunluğu bu model için ölçülemedi.</p>
    );
  }
  if (!health) return <p className="muted small">Baskı uygunluğu ölçülüyor…</p>;

  return (
    <div className="meshhealth">
      <p className="meshhealth__headline">
        <StatusBadge tone={TONE[health.readiness]}>
          {HEADLINE[health.readiness]}
        </StatusBadge>
      </p>
      {health.messages.length > 0 && (
        <ul className="meshhealth__issues">
          {health.messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}
      {/*
        Shown whenever the model contains created surface, in both modes. In
        precise mode the server has already turned this into a blocker and the
        sentence above says so; here it is the plain fact about the file, which
        is what somebody exporting it needs either way.
      */}
      {health.contains_ai_completed && (
        <Callout tone="caution" title="Yapay zekâ ile tamamlanan geometri içeriyor">
          Bu modelin bir bölümünü hiçbir fotoğraf görmedi; o bölge yapay olarak
          üretildi. Modeli görüntüleyicide "Geometri güveni" görünümüne alarak
          hangi bölgelerin ölçüldüğünü görebilirsiniz.
        </Callout>
      )}
      {/*
        Stated even when everything passed, because the reader would otherwise
        take "print ready" to include a thickness check it does not include.
      */}
      <Callout tone="neutral">
        Bu değerlendirme yüzeyin kendisiyle ilgilidir; modelin gerçek boyutunu
        içermez. Minimum duvar kalınlığı ölçülmez.
        {health.provenance_recorded === false &&
          " Bu model, yüzeyin nereden geldiği kaydedilmeye başlanmadan önce üretilmiş."}
      </Callout>
    </div>
  );
}
